/**
 * @file
 * This file contains input utility functions specific to dropdown single select and designed to transform input values.
 * Those provide safe defaults for various properties that causes potential runtime/visual/a11y issues
 * if supplied with incorrect data.
 *
 * Note: All transform functions within this file must be defined using the classic function syntax.
 */

import { withDefaultString } from '@backbase/ui-ang/base-classes';

/**
 * Transforms the provided options array for dropdown input property.
 *
 * @remarks
 * This transform function ensures the options property always receives a valid array.
 * Used as an Angular input transform to provide safe defaults and prevent runtime errors
 * when invalid data is provided to the options input.
 *
 * @internal
 *
 * Returns the provided value if it's an array, otherwise returns an empty array.
 *
 * @param value - The option list to use. If not an array, it defaults to an empty array.
 * @returns An array of dropdown options
 *
 * @example
 *
 * ```typescript
 * @Input({ transform: withDefaultOptions }) options: Array<Object | string> = [];
 * ```
 */
export function withDefaultOptions(value?: unknown): Array<Object | string> {
  if (Array.isArray(value)) {
    return value;
  }

  return [];
}

/**
 * Transforms the provided icon name for dropdown input property.
 *
 * @remarks
 * This transform function ensures the iconName property always receives a valid string value.
 * Used as an Angular input transform to provide safe defaults when invalid data is provided
 * to the iconName input. Defaults to 'toggle-down' icon.
 *
 * @internal
 *
 * This function checks if the provided value is a non-empty string. If yes, it returns the trimmed value,
 * otherwise it defaults to `'toggle-down'`.
 *
 * @param value - The icon name to use. If not a valid string, it defaults to `'toggle-down'`.
 * @returns The provided icon name (trimmed) or the default value 'toggle-down'.
 *
 * @example
 *
 * ```typescript
 * @Input({ transform: withDefaultIconName }) iconName = 'toggle-down';
 * ```
 */
export function withDefaultIconName(value?: unknown): string {
  return withDefaultString(value, 'toggle-down');
}

/**
 * Transforms the provided comparison function for dropdown input property.
 *
 * @remarks
 * This transform function ensures the compareWithFn property always receives a valid function.
 * Used as an Angular input transform to provide safe defaults when invalid data is provided
 * to the compareWithFn input. Defaults to strict equality comparison.
 *
 * @internal
 *
 * Returns the provided value if it's a function, otherwise returns a default equality comparison function.
 *
 * @param value - transform function to use for comparing options. If not a valid function, it defaults to a function that checks for strict equality.
 * @returns A comparison function that takes two parameters and returns a boolean
 *
 * @example
 *
 * ```typescript
 * @Input({ transform: withDefaultCompareWithFn }) compareWithFn: (a: any, b: any) => boolean = (a, b) => a === b;
 * ```
 */
export function withDefaultCompareWithFn(value?: unknown): (a: any, b: any) => boolean {
  if (typeof value === 'function') {
    return value as (a: any, b: any) => boolean;
  }

  return (a: any, b: any) => a === b;
}
